import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { BinaryIcon, Loader2, Mail } from 'lucide-react';
import { useMutation } from '@apollo/client';

import {
  Button,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sandtech-hub-monorepo/ui-kit';
import { ResendVerificationCodeDocument } from '@sandtech-hub-monorepo/ehub-client';

import {
  confirmSignUpLinkSent,
  emailLabel,
  emailPlaceholder,
  signUpButtonLabel,
} from 'libs/ui-auth-kit/src/stringsManager';

const formSchema = z.object({
  email: z.string().email().min(1),
  confirmationCode: z.string().min(1),
});

interface ConfirmSignUpFormProps {
  onSubmit: (values: z.infer<typeof formSchema>) => void;
  defaultValues?: Partial<z.infer<typeof formSchema>>;
  loading: boolean;
  className?: string;
  /** The code arrives in the email link, so there is no code input. */
  codeByLink?: boolean;
  /** Why the email link failed; replaces the "we sent a link" note. */
  linkError?: string;
}

export function ConfirmSignUpForm({
  className,
  loading,
  onSubmit,
  codeByLink,
  linkError,
  ...props
}: ConfirmSignUpFormProps) {
  const [resendVerificationCode] = useMutation(ResendVerificationCodeDocument);
  const [timer, setTimer] = useState(0);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      confirmationCode: props.defaultValues?.confirmationCode ?? '',
    },
  });

  useEffect(() => {
    form.resetField('email', { defaultValue: props.defaultValues?.email });
  }, [props.defaultValues]);

  const timeOutCallback = () =>
    setTimer((currTimer) => (currTimer > 0 ? currTimer - 1 : 0));

  useEffect(() => {
    if (!timer) return;

    const t = setTimeout(timeOutCallback, 1000);

    return () => {
      clearTimeout(t);
    };
  }, [timer, timeOutCallback]);

  const resetTimer = function () {
    if (!timer) {
      setTimer(150);
    }
  };

  const getFormattedTimer = () => {
    const minutes = Math.floor(timer / 60);
    const secs = timer % 60;

    return `${minutes}:${secs}`;
  };

  // Opened from the email link: the code is already filled in.
  const hasCode = !!props.defaultValues?.confirmationCode;

  return (
    <div className={cn('grid gap-6', className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">{emailLabel}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={emailPlaceholder}
                    {...field}
                    endAdornment={<Mail />}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {codeByLink && !hasCode && (
            <p
              className={cn(
                'text-sm',
                linkError ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {linkError ?? confirmSignUpLinkSent}
            </p>
          )}

          {!codeByLink && !hasCode && (
            <FormField
              control={form.control}
              name="confirmationCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    Verification Code
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter the verification code received on your email."
                      endAdornment={<BinaryIcon />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="text-sm text-center">
            <Button
              type="button"
              variant="link"
              disabled={!!timer || form.watch('email') === ''}
              onClick={() => {
                resetTimer();
                const email = form.getValues('email');

                if (email) {
                  resendVerificationCode({
                    variables: { input: { email } },
                    onCompleted: () => {
                      toast(
                        codeByLink
                          ? 'A new confirmation link has been sent to your email address.'
                          : 'The verification code has been sent to your email address.'
                      );
                    },
                  });
                }
              }}
            >
              {codeByLink ? 'Resend Link' : 'Resend Code'}
              <span className="text-foreground ml-1">
                {timer !== 0 && `in ${getFormattedTimer()}`}
              </span>
            </Button>
          </div>

          {(!codeByLink || hasCode) && (
            <Button
              disabled={
                loading ||
                !(form.watch('email') && form.watch('confirmationCode'))
              }
              type="submit"
              className="w-full"
            >
              {loading ? (
                <Loader2 className="animate-spin" />
              ) : (
                signUpButtonLabel
              )}
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
}
